import React from "react";
import { motion } from "framer-motion";
import { Wallet, TrendingUp, Banknote, Check, Layers, Sparkles } from "lucide-react";

const Bold = ({ children }) => (
  <span className="text-white font-semibold">{children}</span>
);

const benefits = [
  {
    icon: Wallet,
    gradient: "from-blue-500 to-indigo-600",
    accent: "text-blue-400",
    glow: "rgba(59,130,246,0.35)",
    eyebrow: "Reduce software spend",
    title: "Replace 5–10 of the tools you pay for today.",
    body: (
      <>
        One platform absorbs your <Bold>PMS, IT spend, VoIP, communication tools,
        revenue intelligence, ePrescribe, eClaims, online booking, payments</Bold> & more —
        a stack of vendor bills collapses into a single line item.
      </>
    ),
    stat: { value: "5–10", label: "softwares replaced" },
  },
  {
    icon: TrendingUp,
    gradient: "from-violet-500 to-purple-600",
    accent: "text-violet-400",
    glow: "rgba(139,92,246,0.35)",
    eyebrow: "Increase production",
    title: "8 front-office AI agents booking appointments 24/7.",
    body: (
      <>
        AI Receptionist, AI Hygiene Recall, AI Case Acceptance, AI Schedule Optimizer & more
        work <Bold>around the clock</Bold> to fill the schedule. The bottom quartile of clients
        lifts production by <Bold>~$3,800/mo</Bold> — some have grown <Bold>$20k/mo+</Bold>.
      </>
    ),
    stat: { value: "+$3.8k/mo", label: "bottom quartile · up to $20k+" },
  },
  {
    icon: Banknote,
    gradient: "from-emerald-500 to-teal-600",
    accent: "text-emerald-400",
    glow: "rgba(16,185,129,0.35)",
    eyebrow: "Cut labor cost & speed up cash flow",
    title: "AI completes 65–75% of your revenue cycle — no review.",
    body: (
      <>
        Submitting claims & pre-auths, posting EOBs, fighting denials and more run
        <Bold> end-to-end without staff touching them</Bold> — shrinking labor cost and pulling
        cash into the practice faster.
      </>
    ),
    stat: { value: "65–75%", label: "of revenue cycle automated" },
  },
];

const Top3BenefitsSlide = () => {
  return (
    <div className="h-full w-full bg-[#0A0A12] flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background gradient + glow layers */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12] via-[#0d1224] to-[#0A0A12]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[520px] bg-blue-600/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[100px]" />

      <div className="max-w-[1280px] w-full mx-auto flex flex-col h-full relative z-10">
        {/* Eyebrow + Title */}
        <div className="text-center mb-4 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm mb-4"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-medium text-blue-300/90">The cut &amp; dry ROI</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-[2.6rem] font-bold text-white tracking-tight leading-[1.08]"
          >
            3 immediate{" "}
            <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
              ROI wins.
            </span>
          </motion.h2>
        </div>

        {/* Framing banner */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex-shrink-0 rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-black/30 px-6 py-4 mb-5 flex items-start gap-4"
        >
          <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600">
            <Layers className="w-[22px] h-[22px] text-white" strokeWidth={2.3} />
          </div>
          <p className="text-[16px] text-white/60 leading-[1.6]">
            Asha is the only <Bold>AI operating system for dental</Bold> — 10+ softwares and 20+ AI
            products in one platform. Our PMS is just <Bold>1 of 30</Bold> products in the OS. The
            platform's compounding benefits far outweigh the "cut &amp; dry" ROI — but here's that
            recap anyway.
          </p>
        </motion.div>

        {/* Benefit cards */}
        <div className="grid grid-cols-3 gap-5 flex-1 min-h-0">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.eyebrow}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.5, ease: "easeOut" }}
                className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-black/30 p-6 flex flex-col overflow-hidden"
              >
                {/* top accent glow */}
                <div
                  className="absolute -top-px left-6 right-6 h-px"
                  style={{ background: `linear-gradient(to right, transparent, ${b.glow}, transparent)` }}
                />

                {/* Icon + number */}
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${b.gradient}`}
                  >
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.3} />
                  </div>
                  <span className="text-[44px] font-bold leading-none tracking-tight text-white/[0.08]">
                    {i + 1}
                  </span>
                </div>

                <div className={`text-[12px] font-bold uppercase tracking-[0.1em] mb-1.5 ${b.accent}`}>
                  {b.eyebrow}
                </div>
                <h3 className="text-[20px] font-bold text-white leading-snug mb-3">{b.title}</h3>
                <p className="text-[15px] text-white/55 leading-[1.6] flex-1">{b.body}</p>

                {/* Stat chip */}
                <div className="mt-4 rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 flex items-baseline gap-2">
                  <Check className={`w-4 h-4 self-center flex-shrink-0 ${b.accent}`} strokeWidth={3} />
                  <span className={`text-[22px] font-bold tracking-tight ${b.accent}`}>
                    {b.stat.value}
                  </span>
                  <span className="text-[13px] font-medium text-white/40 leading-tight">
                    {b.stat.label}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Top3BenefitsSlide;
